import type { AdminConsent, AdminDeletionRequest } from '@/admin/adminApi';
import { ABSENT, formatInstant } from '@/admin/users/accountPresentation';

/**
 * Vocabulaire RGPD de la fiche de compte.
 *
 * Le serveur rend des codes (`health_data_processing`, `completed`,
 * `administration`) : ce sont des valeurs de colonne, pas des phrases. Les
 * afficher tels quels demanderait a l'exploitant de connaitre le schema, ce que
 * la cible interdit explicitement — « aucun jargon d'implementation ».
 *
 * **Un code inconnu est rendu tel quel**, jamais masque ni replie sur « Autre ».
 * Le jour ou le produit gagnera un consentement, l'ecran doit le montrer, meme
 * imparfaitement : un consentement invisible serait un consentement qu'on croit
 * absent.
 */

const CONSENT_LABELS: Readonly<Record<string, string>> = {
  privacy_policy: 'Politique de confidentialité',
  terms: 'Conditions générales',
  health_data_processing: 'Traitement des données de santé',
  analytics_optional: 'Mesure d’audience (facultative)',
  push_notifications: 'Notifications sur le téléphone',
};

export const consentLabel = (type: string): string => CONSENT_LABELS[type] ?? type;

/**
 * Etat d'un consentement, **en toutes lettres**.
 *
 * Une pastille verte ou grise ne suffit pas : l'information ne doit jamais etre
 * portee par la seule couleur, et « accordé » / « refusé » est exactement ce
 * qu'un exploitant doit pouvoir lire a voix haute a la personne qui appelle.
 */
export const consentStateLabel = (consent: AdminConsent): string =>
  consent.granted ? 'Accordé' : 'Refusé';

export const consentVersionLabel = (consent: AdminConsent): string =>
  consent.policyVersion === null || consent.policyVersion === '' ? ABSENT : consent.policyVersion;

export const consentUpdatedLabel = (consent: AdminConsent): string =>
  formatInstant(consent.updatedAt);

const DELETION_STATUS_LABELS: Readonly<Record<string, string>> = {
  completed: 'Effectuée',
  pending: 'En attente',
  failed: 'Échouée',
};

export const deletionStatusLabel = (request: AdminDeletionRequest): string =>
  DELETION_STATUS_LABELS[request.status] ?? request.status;

/**
 * Origine de la demande.
 *
 * `administration` est le code ecrit par l'anonymisation declenchee depuis cet
 * outil : c'est la seule valeur que le back-office produit lui-meme, et la
 * nommer permet de distinguer, six mois plus tard, une demande faite par la
 * personne d'une demande faite pour elle.
 */
const DELETION_REASON_LABELS: Readonly<Record<string, string>> = {
  administration: 'Demande traitée depuis l’administration',
};

export const deletionReasonLabel = (request: AdminDeletionRequest): string => {
  if (request.reasonCode === null || request.reasonCode === '') {
    return 'Motif non renseigné';
  }

  return DELETION_REASON_LABELS[request.reasonCode] ?? request.reasonCode;
};
